/**
 * `/login` — learner sign-in (SCH Milestone A).
 *
 * Consumer-facing standalone screen (no `<LearnShell>` — pre-auth):
 * Schoology wordmark above a centered card on a soft background.
 * Wires email + password to `trpc.auth.login` — the auth router sets the
 * Better-Auth session cookie via `forwardSetCookie`, so a successful
 * mutation leaves the next request authenticated. On success we
 * invalidate `auth.getCurrentUser` (so every gate re-evaluates) and land
 * on `/catalog`.
 *
 * Hand-rolled controlled form (not `<LoginForm>` from `@mangrove/ui/auth`)
 * because this surface needs `data-testid`s on the inputs + submit, which
 * the shared form doesn't expose.
 *
 * Already-signed-in visitors are bounced to `/catalog` in an effect
 * (never during render — same idiom as `<RequireAuth>`).
 */
import * as React from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ArrowRight, Eye, EyeOff, Leaf } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  PoweredBy,
} from '@mangrove/ui';
import { useQueryClient } from '@tanstack/react-query';
import { trpc } from '../lib/trpc.js';
import { useCurrentUser, wipeClientCaches } from '../lib/auth.js';
import { centreFromHost, useCentreDocumentBranding } from '../lib/centre-branding.js';

/**
 * Search params on `/login` — a reset token carried by the email link.
 * Exported so the generated `routeTree.gen.ts` can name it (TS4023).
 */
export interface LoginSearch {
  /**
   * Password-reset token. Better-Auth's `requestPasswordReset` appends
   * `?token=<token>` to the configured `redirectTo` URL, so `token` is the
   * canonical param. `reset` is accepted as a fallback alias.
   */
  token?: string;
  /** #670: the centre whose sign-in link this is (`?centre=qed`): shows its name and logo. */
  centre?: string;
}

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): LoginSearch => {
    const raw = search.token ?? search.reset;
    const token = typeof raw === 'string' && raw !== '' ? raw : undefined;
    const centre =
      typeof search.centre === 'string' && search.centre !== '' ? search.centre : undefined;
    return {
      ...(token === undefined ? {} : { token }),
      ...(centre === undefined ? {} : { centre }),
    };
  },
  component: LoginPage,
});

type LoginMode = 'signin' | 'forgot' | 'reset';

function LoginPage(): React.JSX.Element {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const queryClient = useQueryClient();
  const me = useCurrentUser();
  const search = Route.useSearch();
  const token = search.token;
  // #668: a ?centre= sign-in link, else the centre's own sub-address.
  const centre = search.centre ?? centreFromHost() ?? undefined;

  // A reset token in the URL drops the visitor straight into the reset step.
  const [mode, setMode] = React.useState<LoginMode>(token ? 'reset' : 'signin');

  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [formError, setFormError] = React.useState<string | null>(null);

  // Forgot-password (request a reset email) state.
  const [forgotEmail, setForgotEmail] = React.useState('');
  const [forgotSent, setForgotSent] = React.useState(false);

  // Reset-password (apply the new password with the token) state.
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [resetDone, setResetDone] = React.useState(false);

  const activateCentre = trpc.tenant.activate.useMutation();
  const login = trpc.auth.login.useMutation({
    onSuccess: async () => {
      // §3.3 shared-device wipe, run UNCONDITIONALLY on every successful
      // sign-in (simpler + safer than diffing the previously-cached user —
      // a same-user re-login merely pays one cold cache load). AWAITED
      // before navigate so no stale bucket survives into the next screen.
      await wipeClientCaches(queryClient);
      // #668: signing in through a centre's own link (or, later, its own
      // address) opens THAT centre, when the student belongs to it. Otherwise
      // they land where they would anyway.
      if (centre) {
        try {
          // The address may be the centre's own name (gnosis) rather than its
          // workspace (majed-demo); the server says which workspace it is.
          const found = await utils.branding.publicForCentre.fetch({ centre });
          const slug = (found?.workspace ?? centre).toLowerCase();
          const workspaces = await utils.tenant.list.fetch();
          const target = workspaces.find((w) => w.slug.toLowerCase() === slug);
          if (target) {
            await activateCentre.mutateAsync({ tenantId: target.tenantId });
            // Wipe AGAIN, after the switch (switchTenant's order). Reads that
            // ran while the centre was being opened, such as the live-update
            // stream reconnecting after sign-in and refetching everything,
            // answered for the default workspace. The 30 Sep walk caught one
            // landing after the switch: QED's link opened QED's courses under
            // "Schoology" in the top bar and the centre picker, even after a
            // reload, because the stale answer was also kept on the device.
            await wipeClientCaches(queryClient);
          }
        } catch {
          // Opening the centre is a convenience; the sign-in itself succeeded.
        }
      }
      await utils.auth.getCurrentUser.invalidate();
      void navigate({ to: '/catalog', replace: true });
    },
  });

  const sendReset = trpc.auth.sendPasswordReset.useMutation({
    onSuccess: () => setForgotSent(true),
  });

  const resetPassword = trpc.auth.resetPassword.useMutation({
    onSuccess: () => setResetDone(true),
  });

  // Already signed in → straight to the catalog.
  React.useEffect(() => {
    if (!me.isPending && me.data) {
      void navigate({ to: '/catalog', replace: true });
    }
  }, [me.isPending, me.data, navigate]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (login.isPending) return;
    if (!email.trim() || !password) {
      setFormError('Enter your email and password.');
      return;
    }
    setFormError(null);
    login.mutate({ email: email.trim(), password });
  };

  const handleForgotSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (sendReset.isPending) return;
    if (!forgotEmail.trim()) {
      setFormError('Enter your email.');
      return;
    }
    setFormError(null);
    sendReset.mutate({ email: forgotEmail.trim() });
  };

  const handleResetSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (resetPassword.isPending) return;
    if (!token) {
      setFormError('This reset link is invalid or has expired.');
      return;
    }
    if (newPassword.length < 8) {
      setFormError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword.length > 128) {
      setFormError('Password must be 128 characters or fewer.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setFormError('Passwords do not match.');
      return;
    }
    setFormError(null);
    resetPassword.mutate({ token, newPassword });
  };

  /** Return to the sign-in view, clearing any transient step state. */
  const backToSignIn = (): void => {
    setFormError(null);
    setForgotSent(false);
    setMode('signin');
  };

  return (
    <div className="page-canvas flex min-h-dvh flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-md space-y-6">
        <SchoologyWordmark centre={centre} />
        {mode === 'signin' ? (
          <>
            <Card className="card-soft animate-fade-in-up w-full">
              <CardHeader className="space-y-3">
                {/* The product's own line; on a centre's link, the centre speaks
                    for itself (walk, 25 Sep: QED showed Schoology's tagline). */}
                {centre === undefined ? (
                  <Badge
                    variant="secondary"
                    className="w-fit gap-1.5 rounded-full bg-primary/10 text-xs font-semibold tracking-wide text-primary"
                  >
                    <span
                      className="size-1.5 rounded-full bg-primary ring-4 ring-primary/20"
                      aria-hidden="true"
                    />
                    SAT · GRE · Medical Admission
                  </Badge>
                ) : null}
                <div className="space-y-1">
                  <CardTitle className="text-2xl tracking-tight">Welcome back</CardTitle>
                  <CardDescription>Sign in to keep practicing.</CardDescription>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} noValidate className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      data-testid="login-email"
                      type="email"
                      autoComplete="email"
                      placeholder="you@example.com"
                      className="min-h-touch transition-shadow focus-visible:ring-2 focus-visible:ring-primary/40"
                      disabled={login.isPending}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <Label htmlFor="login-password">Password</Label>
                      <button
                        type="button"
                        data-testid="forgot-password-link"
                        onClick={() => {
                          setFormError(null);
                          setForgotEmail(email.trim());
                          setMode('forgot');
                        }}
                        className="text-xs font-semibold text-primary underline-offset-2 hover:underline"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <div className="relative">
                      <Input
                        id="login-password"
                        data-testid="login-password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        placeholder="Enter your password"
                        className="min-h-touch pr-11 transition-shadow focus-visible:ring-2 focus-visible:ring-primary/40"
                        disabled={login.isPending}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        className="absolute right-1 top-1/2 grid size-9 -translate-y-1/2 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                      >
                        {showPassword ? (
                          <EyeOff className="size-4" aria-hidden="true" />
                        ) : (
                          <Eye className="size-4" aria-hidden="true" />
                        )}
                      </button>
                    </div>
                  </div>
                  {(formError ?? login.error) ? (
                    <p
                      role="alert"
                      data-testid="login-error"
                      className="text-sm font-medium text-destructive"
                    >
                      {formError ?? login.error?.message}
                    </p>
                  ) : null}
                  <Button
                    type="submit"
                    data-testid="login-submit"
                    disabled={login.isPending}
                    className="press group min-h-touch w-full"
                  >
                    {login.isPending ? 'Signing in…' : 'Sign in'}
                    {!login.isPending ? (
                      <ArrowRight
                        className="size-4 transition-transform group-hover:translate-x-0.5"
                        aria-hidden="true"
                      />
                    ) : null}
                  </Button>
                </form>
              </CardContent>
            </Card>
            <p className="text-center text-sm text-muted-foreground">
              New here?{' '}
              <Link
                to="/signup"
                className="font-semibold text-primary underline-offset-4 hover:underline"
              >
                Create an account
              </Link>
            </p>
            <p className="text-center text-xs text-muted-foreground">
              By signing in you agree to our Terms and Privacy Policy.
            </p>
          </>
        ) : null}

        {mode === 'forgot' ? (
          <Card className="card-soft animate-fade-in-up w-full">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl tracking-tight">Reset your password</CardTitle>
              <CardDescription>
                Enter your email and we&apos;ll send you a reset link.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {forgotSent ? (
                <div className="space-y-4">
                  <p
                    role="status"
                    data-testid="forgot-sent"
                    className="text-sm text-muted-foreground"
                  >
                    If an account exists for that email, a reset link has been sent. Check your
                    inbox.
                  </p>
                  <Button type="button" onClick={backToSignIn} className="press min-h-touch w-full">
                    Back to sign in
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleForgotSubmit} noValidate className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email">Email</Label>
                    <Input
                      id="forgot-email"
                      data-testid="forgot-email-input"
                      type="email"
                      autoComplete="email"
                      placeholder="you@example.com"
                      className="min-h-touch transition-shadow focus-visible:ring-2 focus-visible:ring-primary/40"
                      disabled={sendReset.isPending}
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                    />
                  </div>
                  {(formError ?? sendReset.error) ? (
                    <p
                      role="alert"
                      data-testid="forgot-error"
                      className="text-sm font-medium text-destructive"
                    >
                      {formError ?? sendReset.error?.message}
                    </p>
                  ) : null}
                  <Button
                    type="submit"
                    data-testid="forgot-submit"
                    disabled={sendReset.isPending}
                    className="press min-h-touch w-full"
                  >
                    {sendReset.isPending ? 'Sending…' : 'Send reset link'}
                  </Button>
                  <button
                    type="button"
                    onClick={backToSignIn}
                    className="block w-full text-center text-sm font-medium text-primary underline-offset-4 hover:underline"
                  >
                    Back to sign in
                  </button>
                </form>
              )}
            </CardContent>
          </Card>
        ) : null}

        {mode === 'reset' ? (
          <Card className="card-soft animate-fade-in-up w-full">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl tracking-tight">Choose a new password</CardTitle>
              <CardDescription>
                {resetDone
                  ? 'Your password has been updated.'
                  : 'Enter a new password for your account.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {resetDone ? (
                <div className="space-y-4">
                  <p
                    role="status"
                    data-testid="reset-done"
                    className="text-sm text-muted-foreground"
                  >
                    Your password has been reset. You can now sign in with your new password.
                  </p>
                  <Button
                    type="button"
                    onClick={() => {
                      setFormError(null);
                      setMode('signin');
                    }}
                    className="press min-h-touch w-full"
                  >
                    Back to sign in
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleResetSubmit} noValidate className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reset-password">New password</Label>
                    <Input
                      id="reset-password"
                      data-testid="reset-password-input"
                      type="password"
                      autoComplete="new-password"
                      className="min-h-touch transition-shadow focus-visible:ring-2 focus-visible:ring-primary/40"
                      disabled={resetPassword.isPending}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reset-confirm">Confirm new password</Label>
                    <Input
                      id="reset-confirm"
                      data-testid="reset-confirm-input"
                      type="password"
                      autoComplete="new-password"
                      className="min-h-touch transition-shadow focus-visible:ring-2 focus-visible:ring-primary/40"
                      disabled={resetPassword.isPending}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                  </div>
                  {(formError ?? resetPassword.error) ? (
                    <p
                      role="alert"
                      data-testid="reset-error"
                      className="text-sm font-medium text-destructive"
                    >
                      {formError ?? resetPassword.error?.message}
                    </p>
                  ) : null}
                  <Button
                    type="submit"
                    data-testid="reset-submit"
                    disabled={resetPassword.isPending}
                    className="press min-h-touch w-full"
                  >
                    {resetPassword.isPending ? 'Updating…' : 'Update password'}
                  </Button>
                  <button
                    type="button"
                    onClick={() => {
                      setFormError(null);
                      setMode('signin');
                    }}
                    className="block w-full text-center text-sm font-medium text-primary underline-offset-4 hover:underline"
                  >
                    Back to sign in
                  </button>
                </form>
              )}
            </CardContent>
          </Card>
        ) : null}
      </div>
      <PoweredBy className="mt-6" />
    </div>
  );
}

/**
 * Brand lockup above the card — same glyph family as `<LearnShell>`.
 *
 * #670: on a centre's own sign-in link (`?centre=qed`) it shows THAT centre's
 * name, and its logo when it has set one. Without the link, or for a name that
 * is not a coaching centre, the default lockup.
 */
function SchoologyWordmark({ centre }: { centre?: string | undefined }): React.JSX.Element {
  const centreQ = trpc.branding.publicForCentre.useQuery(
    { centre: centre ?? '' },
    { enabled: centre !== undefined, staleTime: 5 * 60_000, retry: false },
  );
  const [logoFailed, setLogoFailed] = React.useState(false);
  const found = centre !== undefined ? centreQ.data : null;
  // On a centre's link, no default name flashes up while the centre loads.
  const name = centre !== undefined && centreQ.isLoading ? '' : (found?.name ?? 'Schoology');
  const logo = found?.logoUrl && !logoFailed ? found.logoUrl : null;
  // #668: the tab names the centre too ("Sign in · QED"), not the default app.
  useCentreDocumentBranding(found?.name ?? null, found?.logoUrl ?? null, 'Sign in');
  return (
    <div
      className="animate-fade-in flex items-center justify-center gap-2.5 text-2xl font-semibold tracking-tight"
      data-testid="login-wordmark"
    >
      {logo ? (
        <img
          src={logo}
          alt=""
          className="size-10 rounded-lg object-contain"
          onError={() => setLogoFailed(true)}
        />
      ) : (
        <span className="flex size-9 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-brand-blue shadow-sm">
          <Leaf className="size-5 text-primary-foreground" aria-hidden="true" />
        </span>
      )}
      <span className="bg-gradient-to-r from-primary to-brand-blue bg-clip-text text-transparent">
        {name}
      </span>
    </div>
  );
}
